import { useEffect, useRef } from 'react';

interface ParticleConfig {
  count: number;
  maxDist: number;
  speed: number;
  flow: number;
  warp: number;
  depthPush: number;
  glow: number;
}

const LEVELS: Record<string, ParticleConfig> = {
  suave: { count: 92, maxDist: 124, speed: 0.34, flow: 0.16, warp: 0.11, depthPush: 0.07, glow: 0.72 },
  impacto: { count: 138, maxDist: 156, speed: 0.58, flow: 0.28, warp: 0.18, depthPush: 0.11, glow: 1.00 },
  insano: { count: 188, maxDist: 184, speed: 0.86, flow: 0.42, warp: 0.26, depthPush: 0.17, glow: 1.34 },
};

interface Dot {
  x: number;
  y: number;
  z: number;
  w: number;
  vx: number;
  vy: number;
  r: number;
  _px?: number;
  _py?: number;
  _depth?: number;
}

export default function ParticleCanvas({ level = 'impacto' }: { level?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let W: number, H: number;
    let dots: Dot[] = [];
    let t = 0;
    const pointer = { x: 0, y: 0, active: false };
    let scrollTarget = 0;
    let scrollEnergy = 0;

    const CFG: ParticleConfig = { ...LEVELS[level] };
    const isInsano = level === 'insano';

    function resize() {
      W = canvas!.width = window.innerWidth;
      H = canvas!.height = window.innerHeight;
    }

    function randomDot(): Dot {
      return {
        x: Math.random() * W,
        y: Math.random() * H,
        z: Math.random() * Math.PI * 2,
        w: Math.random() * Math.PI * 2,
        vx: (Math.random() - 0.5) * CFG.speed,
        vy: (Math.random() - 0.5) * CFG.speed,
        r: Math.random() * 2.2 + 0.8,
      };
    }

    function init() {
      resize();
      dots = Array.from({ length: CFG.count }, randomDot);
    }

    function draw() {
      t += 0.008;
      scrollEnergy += (scrollTarget - scrollEnergy) * 0.06;

      if (isInsano) {
        ctx!.fillStyle = 'rgba(3,4,10,0.24)';
        ctx!.fillRect(0, 0, W, H);
      } else {
        ctx!.clearRect(0, 0, W, H);
      }

      const beat = isInsano ? ((Math.sin(t * 8.8) + 1) * 0.5) : 0;
      const pulse = isInsano ? (1 + beat * 0.58 + scrollEnergy * 0.72) : 1;
      const centerX = W * 0.5;
      const centerY = H * 0.5;

      dots.forEach((d, i) => {
        const flow = Math.sin((d.y / H) * 6 + t + d.w) * CFG.flow * pulse;
        d.x += d.vx + flow;
        d.y += d.vy + Math.cos((d.x / W) * 5 + t + d.z) * CFG.warp * pulse;
        d.z += 0.022;
        d.w += 0.016;

        if (pointer.active) {
          const dxp = pointer.x - d.x;
          const dyp = pointer.y - d.y;
          const pull = 1 / (1 + (dxp * dxp + dyp * dyp) / 18000);
          d.x += dxp * 0.0035 * pull;
          d.y += dyp * 0.0035 * pull;
        }

        if (d.x < 0) d.x = W;
        if (d.x > W) d.x = 0;
        if (d.y < 0) d.y = H;
        if (d.y > H) d.y = 0;

        const depth = (Math.sin(d.z + t * 1.7) + 1) * 0.5;
        const warp = (Math.cos(d.w + t * 1.2) + 1) * 0.5;
        const depthPush = CFG.depthPush * pulse;
        const px = d.x + (d.x - centerX) * (depth - 0.5) * depthPush;
        const py = d.y + (d.y - centerY) * (warp - 0.5) * (CFG.depthPush * 0.82);
        const radius = d.r * (0.55 + depth * 1.35) * (isInsano ? (0.9 + beat * 0.42) : 1);

        const pinkAlpha = Math.min((0.22 + depth * 0.38) * CFG.glow * pulse, 0.96);
        const cyanAlpha = Math.min((0.08 + warp * 0.24) * CFG.glow * pulse, 0.82);

        ctx!.beginPath();
        ctx!.arc(px, py, radius, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(122,0,255,${pinkAlpha})`;
        ctx!.fill();

        ctx!.beginPath();
        ctx!.arc(px, py, radius * 0.45, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(0,240,255,${cyanAlpha})`;
        ctx!.fill();

        if (isInsano && (i % 9 === 0) && depth > 0.78) {
          ctx!.beginPath();
          ctx!.arc(px, py, radius * (1.9 + beat * 0.8), 0, Math.PI * 2);
          ctx!.strokeStyle = `rgba(255,0,200,${0.1 + beat * 0.15})`;
          ctx!.lineWidth = 0.7 + beat * 0.8;
          ctx!.stroke();
        }

        d._px = px;
        d._py = py;
        d._depth = depth;
      });

      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i]._px! - dots[j]._px!;
          const dy = dots[i]._py! - dots[j]._py!;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CFG.maxDist) {
            const depthMix = (dots[i]._depth! + dots[j]._depth!) * 0.5;
            const alpha = (1 - dist / CFG.maxDist) * (0.06 + depthMix * 0.18) * pulse;
            const isPink = ((i + j + Math.floor(t * 10)) % 3) !== 0;
            ctx!.beginPath();
            ctx!.moveTo(dots[i]._px!, dots[i]._py!);
            ctx!.lineTo(dots[j]._px!, dots[j]._py!);
            ctx!.strokeStyle = isPink
              ? `rgba(122,0,255,${alpha})`
              : `rgba(0,240,255,${alpha})`;
            ctx!.lineWidth = (0.6 + depthMix * 1.2) * (isInsano ? (0.9 + beat * 0.5) : 1);
            ctx!.stroke();
          }
        }
      }

      animRef.current = requestAnimationFrame(draw);
    }

    const handlePointerMove = (ev: PointerEvent) => {
      pointer.x = ev.clientX;
      pointer.y = ev.clientY;
      pointer.active = true;
    };
    const handlePointerLeave = () => {
      pointer.active = false;
    };
    const handleScroll = () => {
      const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const pct = window.scrollY / maxScroll;
      scrollTarget = Math.min(Math.max(pct, 0), 1);
    };
    const handleResize = () => {
      resize();
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', handlePointerLeave, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    init();
    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [level]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />
  );
}
